define('presentation/signup/viewModels/signupPlansMultiSelectViewModel',[
    'i18next',
    'businessServices/converters/currencyFormatter',
    'businessServices/signup/signupSessionStore',
    'common/promises/promiseFactory',
    'common/storage/workSessionIdStorage',
    'constants/billingPackageTypeConstants',
    'presentation/signup/presentationObjects/planPresentationObject',
], function () {
    return function () {
        const self = this;

        const CurrencyFormatterConstructor = require('businessServices/converters/currencyFormatter');
        const _currencyFormatter = new CurrencyFormatterConstructor();

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const PlanPresentationObjectConstructor = require('presentation/signup/presentationObjects/planPresentationObject');

        const _i18n = require('i18next');
        const _billingPackageTypes = require('constants/billingPackageTypeConstants');
        const _workSessionIdStorage = require('common/storage/workSessionIdStorage');

        const _isSignupForCustomer = _workSessionIdStorage.getWorkSessionId() != null;

        let _signupSessionStore = null;
        let _availablePackages = [];
        let _isPreviousPageValid = false;
        let _isPackageDeepLinked = false;
        let _onPackageSelected = null;
        let _onPackageRemoved = null;
        let _disposables = [];

        self.showPlans = ko.observable(true);
        self.showPackagePicker = ko.observable(false);

        self.isNavigateToNextPageActive = ko.pureComputed(() => {
            return _isSignupForCustomer || self.isAnyPlanSelected();
        });
        self.isNavigateToNextPageVisible = ko.pureComputed(() => self.showPlans());
        self.isNavigateToPreviousPageVisible = ko.pureComputed(() => {
            return self.showPackagePicker() || _isPreviousPageValid;
        });
        self.onForwardArrowClicked = null;
        self.onBackArrowClickedCallback = null;
        self.onForwardArrowClickedCallback = null;

        self.selectedPackages = ko.observableArray([]);

        self.packagePickerAvailablePackages = ko.observableArray([]);
        self.packagePickerPackageId = ko.observable(null);
        self.packagePickerPackageType = ko.observable(null);

        self.isWideTableColumn = ko.pureComputed(() => self.showPackagePicker());

        const SERVICE_PACKAGE_TYPES = [
            _billingPackageTypes.liveReceptionist,
            _billingPackageTypes.outboundCalling,
            _billingPackageTypes.webChat,
        ];

        const _packagePickerCopy = (key) => {
            const packageType = SERVICE_PACKAGE_TYPES
                .find(serviceType => serviceType.id === self.packagePickerPackageType());

            return packageType === undefined ? '' : _i18n.t('signupPlans:' + key, { context: packageType.key });
        };

        self.headingText = ko.pureComputed(() => {
            if (self.showPackagePicker()) {
                return _packagePickerCopy('headingTextPackagePicker');
            }

            return _isPackageDeepLinked ?
                _i18n.t('signupPlans:headingTextMultiSelectSelected') :
                _i18n.t('signupPlans:headingTextMultiSelect');
        });

        self.subtitleText = ko.pureComputed(() => {
            if (self.showPackagePicker()) {
                return _packagePickerCopy('subtitleTextPackagePicker');
            }

            return _isPackageDeepLinked ?
                _i18n.t('signupPlans:subtitleTextMultiSelectSelected') :
                _i18n.t('signupPlans:subtitleTextMultiSelect');
        });

        self.showPlanSelectionError = ko.observable(false);
        self.isAnyPlanSelected = ko.pureComputed(() => self.selectedPackages().some(p => p.packageId() != null));

        const _navigateToPlans = () => {
            self.showPlans(true);
            self.showPackagePicker(false);
        };

        const _navigateToPackagePicker = () => {
            self.showPlans(false);
            self.showPackagePicker(true);
        };

        const _updatePackageTypeSelection = (type) => {
            return _promiseFactory.defer((promise) => {
                _signupSessionStore.savePackageTypeSelection(type)
                    .done(promise.resolve);
            });
        };

        const readableValue = (value) => value === 999999 ? "Unlimited" : value;

        const _buildIncludedItemText = (translationKey, value) => {
            const count = readableValue(value);

            if (count === 0) {
                return _i18n.t(`${translationKey}_0`);
            }

            if (count === "Unlimited") {
                return _i18n.t(`${translationKey}_unlimited`);
            }

            return _i18n.t(translationKey, {count: parseInt(count, 10)});
        };

        const _formatPlanIncludedItems = (billingPackage) => {
            switch (billingPackage.type) {
                case _billingPackageTypes.liveReceptionist.id:
                    return _buildIncludedItemText('signupPlans:includedMinutes', billingPackage.includedMinutes);
                case _billingPackageTypes.webChat.id:
                    return _buildIncludedItemText('signupPlans:includedChats', billingPackage.includedChatSessions);
                case _billingPackageTypes.outboundCalling.id:
                    return _buildIncludedItemText('signupPlans:includedOutboundContacts', billingPackage.includedOutboundContacts);
                default:
                    return "";
            }
        };

        const _buildEmptyPlanPresentationObject = (type) => {
            const planPresentationObject = new PlanPresentationObjectConstructor();
            planPresentationObject.type(type);
            planPresentationObject.isSignupForCustomer(true);
            return planPresentationObject;
        };

        const _buildPlanPresentationObject = (billingPackage) => {
            const planPresentationObject = new PlanPresentationObjectConstructor();
            planPresentationObject.billingPackageId(billingPackage.billingPackageId);
            planPresentationObject.packageId(billingPackage.packageId);
            planPresentationObject.type(billingPackage.type);
            planPresentationObject.name(billingPackage.name);
            planPresentationObject.includedItems(_formatPlanIncludedItems(billingPackage));
            planPresentationObject.price(_i18n.t('signupPlans:pricePerMonth', {price: _currencyFormatter.formatPlanPrice(billingPackage.pricePerMonth)}));
            planPresentationObject.isSignupForCustomer(true);
            planPresentationObject.isSignatureService(billingPackage.isSignatureService);
            return planPresentationObject;
        };

        const _preparePlanForDisplay = (billingPackage, type) => {
            return billingPackage ? _buildPlanPresentationObject(billingPackage) : _buildEmptyPlanPresentationObject(type);
        };

        self.onEditPlan = (planPresentationObject) => {
            const packages = _availablePackages.filter(p => p.type === planPresentationObject.type());
            switch (planPresentationObject.type()) {
                case _billingPackageTypes.liveReceptionist.id:
                case _billingPackageTypes.webChat.id:
                case _billingPackageTypes.outboundCalling.id:
                    _updatePackageTypeSelection(planPresentationObject.type());
                    self.packagePickerAvailablePackages(packages);
                    self.packagePickerPackageId(planPresentationObject.packageId());
                    self.packagePickerPackageType(planPresentationObject.type());
                    _navigateToPackagePicker();
                    break;
                default:
                    break;
            }
        };

        self.onRemovePlan = (planPresentationObject) => {
            const type = planPresentationObject.type();
            const currentPackage = self.selectedPackages().find(p => p.type() === type);
            if (currentPackage) {
                currentPackage.billingPackageId(null);
                currentPackage.packageId(null);
                currentPackage.name("");
                currentPackage.price("$0");
            }
            if (_onPackageRemoved) {
                _onPackageRemoved(type);
            }
        };

        self.onPackagePickerSelect = (pkg) => {
            const currentPackage = self.selectedPackages().find(p => p.type() === pkg.type);
            if (currentPackage) {
                currentPackage.billingPackageId(pkg.billingPackageId);
                currentPackage.packageId(pkg.packageId);
                currentPackage.name(pkg.name);
                currentPackage.includedItems(_formatPlanIncludedItems(pkg));
                currentPackage.price(_i18n.t('signupPlans:pricePerMonth', { price: pkg.price }));
                currentPackage.isSignatureService(pkg.isSignatureService);
            }
            if (_onPackageSelected) {
                _onPackageSelected(pkg.type, pkg.packageId);
            }

            _updatePackageTypeSelection(null);
            _navigateToPlans();
        };

        self.onBackArrowClicked = () => {
            return _promiseFactory.defer((deferredObject) => {
                if (self.showPlans()) {
                    if (self.onBackArrowClickedCallback) {
                        self.onBackArrowClickedCallback(true)
                            .done(deferredObject.resolve)
                            .fail(deferredObject.reject);
                    } else {
                        deferredObject.resolve();
                    }
                } else {
                    _updatePackageTypeSelection(null);
                    _navigateToPlans();
                    deferredObject.resolve();
                }
            });
        };

        self.onForwardArrowClicked = () => {
            return _promiseFactory.defer((deferredObject) => {
                if (!_isSignupForCustomer && !self.isAnyPlanSelected()) {
                    self.showPlanSelectionError(true);
                    deferredObject.resolve(false);
                    return;
                }

                self.onForwardArrowClickedCallback()
                    .done(deferredObject.resolve)
                    .fail(deferredObject.reject);
            });
        };

        const _refreshSelectedPackages = (selectedPackageIds) => {
            self.selectedPackages.removeAll();
            self.showPlanSelectionError(false);

            const serviceTypes = [
                { type: _billingPackageTypes.liveReceptionist.id, packageId: selectedPackageIds.liveReceptionistPackageId },
                { type: _billingPackageTypes.outboundCalling.id,  packageId: selectedPackageIds.outboundCallingPackageId },
                { type: _billingPackageTypes.webChat.id,          packageId: selectedPackageIds.webChatPackageId },
            ];

            serviceTypes.forEach(({ type, packageId }) => {
                const isSelectable = _availablePackages.some(p => p.type === type);
                if (isSelectable) {
                    const match = _availablePackages.find(p => p.packageId === packageId);
                    self.selectedPackages.push(_preparePlanForDisplay(match, type));
                }
            });
        };

        self.detached = () => {
            _disposables.forEach(s => s.dispose());
            _disposables = [];
        };

        self.activate = ({ availablePackages, isPreviousPageValid, isPackageDeepLinked, selectedPackageIds, onPackageSelected, onPackageRemoved, onForwardArrowClicked, onBackArrowClicked }) => {
            const SignupSessionStoreConstructor = require('businessServices/signup/signupSessionStore');
            _signupSessionStore = new SignupSessionStoreConstructor();
            _signupSessionStore.init();

            _availablePackages = availablePackages;
            _isPreviousPageValid = isPreviousPageValid === true;
            _isPackageDeepLinked = isPackageDeepLinked === true;
            _onPackageSelected = onPackageSelected;
            _onPackageRemoved = onPackageRemoved;

            self.onForwardArrowClickedCallback = onForwardArrowClicked;
            self.onBackArrowClickedCallback = onBackArrowClicked;

            _refreshSelectedPackages(selectedPackageIds);

            _disposables.push(self.isAnyPlanSelected.subscribe((isSelected) => {
                if (isSelected) {
                    self.showPlanSelectionError(false);
                }
            }));

            return _promiseFactory.wait();
        };
    };
});
